import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ExternalLink, X } from 'lucide-react';
import recruiterService from '@/services/recruiterService';
import { toast } from '@/stores/toastStore';

interface PendingPayment {
  paymentUrl: string;
  paymentId: string;
  subscriptionId: string;
  plan: { id: string; name: string; description: string; priceCents: number };
}

export default function PendingPaymentBanner() {
  const { t } = useTranslation('recruiter');
  const [pending, setPending] = useState<PendingPayment | null>(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    recruiterService.getPendingPayment().then((data) => {
      if (data.pending && data.paymentUrl) {
        setPending({
          paymentUrl: data.paymentUrl!,
          paymentId: data.paymentId!,
          subscriptionId: data.subscriptionId!,
          plan: data.plan!,
        });
      }
    }).catch(() => {
      // Silently ignore — non-critical
    });
  }, []);

  const handleDismiss = async () => {
    try {
      setDismissing(true);
      await recruiterService.cancelPendingPayment();
      setPending(null);
    } catch {
      toast.error(t('payment.pending.dismissError'));
      setDismissing(false);
    }
  };

  if (!pending) return null;

  return (
    <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-900">
          {t('payment.pending.title', { planName: pending.plan.name })}
        </p>
        <p className="text-sm text-amber-700 mt-0.5">
          {t('payment.pending.description', {
            price: (pending.plan.priceCents / 100).toFixed(0),
          })}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <a
          href={pending.paymentUrl}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
        >
          {t('payment.pending.resumeButton')}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <button
          onClick={handleDismiss}
          disabled={dismissing}
          className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded transition-colors disabled:opacity-50"
          aria-label={t('payment.pending.dismiss')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
